/**
 * A voice write shown as a change card (`.sk-change`) with a working Undo.
 *
 * An ordinary change card undoes by writing one registry field back through
 * `onFieldUpdate`. A voice is not one field: it spans `personas`,
 * `emotions_yaml` and `voice_gender` (see `sidekickVoiceApply.ts`). So the card
 * carries a snapshot of those three keys from before and after the write, and
 * Undo/Redo restores the snapshot through the host's `onVoiceRestore`.
 *
 * `voice` is not a Sidekick field id on purpose: listing it in the registry
 * would offer it to the LLM as an `update_field` target. It only names the card
 * and the Voice section it points at on the page.
 */
import type { Persona, VoicePreset } from '../../config/types';
import type { VoiceOption } from './sidekickChatMessages';
import { applyVoiceSelection } from './sidekickVoiceApply';

export const VOICE_CHANGE_FIELD_ID = 'voice';
export const VOICE_CHANGE_LABEL = 'Voice';

/** The config keys a voice write touches, as they were (or became). */
export interface VoiceDraft {
  personas?: Persona[];
  emotions_yaml?: string;
  voice_gender?: string;
}

/** What the host hands back after applying a voice, for the change card. */
export interface VoiceChange {
  before: VoiceDraft;
  after: VoiceDraft;
  /** The previous voice's display name, when it could be resolved. */
  beforeLabel: string | null;
}

function voiceDraftOf(config: VoiceDraft): VoiceDraft {
  return {
    personas: config.personas,
    emotions_yaml: config.emotions_yaml,
    voice_gender: config.voice_gender,
  };
}

function defaultVoicePromptId(yaml?: string): string | undefined {
  return yaml ? /^default:\s*"?([^"\s]+)"?/m.exec(yaml)?.[1] : undefined;
}

/**
 * Name the voice a draft uses: the default persona's preset, else the catalog
 * preset whose YAML has the same default voice prompt (a new bot has no
 * personas, only the default emotions YAML). Null when nothing matches.
 */
export function voiceDraftLabel(draft: VoiceDraft, presets?: VoicePreset[] | null): string | null {
  const personas = draft.personas || [];
  const persona = personas.find((p) => p.is_default) || personas[0];
  if (persona?.voice_preset) {
    const preset = presets?.find((p) => p.id === persona.voice_preset);
    if (preset) return preset.name;
  }
  const yaml = persona?.emotions_yaml?.trim() ? persona.emotions_yaml : draft.emotions_yaml;
  const voicePromptId = defaultVoicePromptId(yaml);
  if (voicePromptId) {
    const preset = presets?.find((p) => defaultVoicePromptId(p.yaml) === voicePromptId);
    if (preset) return preset.name;
  }
  return null;
}

/** Compute the writes for picking `voice`, plus the snapshots its change card needs. */
export function planVoiceChange(
  config: VoiceDraft,
  voice: VoiceOption,
  presets?: VoicePreset[] | null,
): VoiceChange {
  const before = voiceDraftOf(config);
  const updates = applyVoiceSelection(config, voice);
  return {
    before,
    // A multi-persona write leaves the top-level keys alone; keep them as they were.
    after: { ...before, ...updates },
    beforeLabel: voiceDraftLabel(before, presets),
  };
}

/** Stored-transcript form of a snapshot. */
export function serializeVoiceDraft(draft: VoiceDraft): string {
  return JSON.stringify(voiceDraftOf(draft));
}

/** Inverse of serializeVoiceDraft; null for anything malformed (e.g. a truncated store). */
export function parseVoiceDraft(raw: string | null | undefined): VoiceDraft | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as VoiceDraft | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.personas !== undefined && !Array.isArray(parsed.personas)) return null;
    return voiceDraftOf(parsed);
  } catch {
    return null;
  }
}
